import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CopyLinkDialog from "components/ui/CopyLinkDialog";
import useApi from "components/Utils/api";
import toast from "utils/toast";

const CURRENCIES = ["AED"];

const INITIAL_FORM = {
  amount: "",
  currency: "AED",
  description: "",
  quantity: "1",
  notifyEmail: false,
  notifyEmailAddress: "",
  notifySms: false,
  notifyPhoneNumber: "",
  schedule: false,
  startDate: "",
  startTime: "00:00",
  attachQr: false,
  hasExpiry: false,
  expiryDate: "",
  expiryTime: "00:00",
};

const NAV_KEYS = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

const allowDecimalKeys = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey || NAV_KEYS.includes(e.key)) return;
  if (e.key === "." && !e.target.value.includes(".")) return;
  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
};

const allowIntegerKeys = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey || NAV_KEYS.includes(e.key)) return;
  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
};

const generateTransactionId = () =>
  `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;

const pad2 = (n) => String(n).padStart(2, "0");

const getNowDateTimeParts = () => {
  const d = new Date();
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
};

const isPastDateTime = (dateStr, timeStr) => {
  if (!dateStr) return false;
  const { date: todayStr, time: nowTimeStr } = getNowDateTimeParts();
  return `${dateStr}T${timeStr || "00:00"}` < `${todayStr}T${nowTimeStr}`;
};

const formatPaymentDate = (dateStr, timeStr) => {
  if (!dateStr) {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${dd}/${mm}/${now.getFullYear()} ${hh}:${min}:${ss}`;
  }
  const [yyyy, mm, dd] = dateStr.split("-");
  const time = timeStr || "00:00";
  return `${dd}/${mm}/${yyyy} ${time}:00`;
};

function NewPaymentLink() {
  const navigate = useNavigate();
  const api = useApi();
  const [controller] = useMaterialUIController();
  const { merchant, sidenavColor } = controller;

  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  const startDateRef = useRef(null);
  const startTimeRef = useRef(null);
  const expiryDateRef = useRef(null);
  const expiryTimeRef = useRef(null);

  useEffect(() => {
    if (merchant?.mid) {
      api.get(`customers`)
        .then((res) => {
          const data = res.data?.data;
          const activeCustomers = (Array.isArray(data) ? data : []).filter(
            (c) => c.status === "ACTIVE" || c.status === true || c.status === 1 || c.status === "1"
          );
          setCustomers(activeCustomers);
        })
        .catch(() => {});
    }
  }, []);

  const handleCustomerChange = (_, customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setForm((prev) => ({
        ...prev,
        quantity: "1",
        notifyEmailAddress: customer.email || prev.notifyEmailAddress,
        notifyPhoneNumber: customer.phoneNumber || prev.notifyPhoneNumber,
      }));
    } else {
      setForm((prev) => ({ ...prev, quantity: "1" }));
    }
  };

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleAmountChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = value.indexOf(".");
    if (firstDot !== -1) {
      value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "");
    }
    setForm((prev) => ({ ...prev, amount: value }));
    setErrors((prev) => ({ ...prev, amount: false }));
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setForm((prev) => ({ ...prev, quantity: value }));
    setErrors((prev) => ({ ...prev, quantity: false }));
  };

  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    setForm((prev) => ({
      ...prev,
      startDate: newStart,
      expiryDate: prev.expiryDate && prev.expiryDate < newStart ? "" : prev.expiryDate,
    }));
    setErrors((prev) => ({ ...prev, startDate: false }));
  };

  const handleExpiryDateChange = (e) => {
    const newExpiry = e.target.value;
    setForm((prev) => ({
      ...prev,
      expiryDate: newExpiry,
      startDate: prev.startDate && prev.startDate > newExpiry ? "" : prev.startDate,
    }));
    setErrors((prev) => ({ ...prev, expiryDate: false }));
  };

  const toggle = (field) => (e) => {
    const checked = e.target.checked;
    setForm((prev) => {
      const next = { ...prev, [field]: checked };
      if (!checked && field === "notifyEmail") next.notifyEmailAddress = "";
      if (!checked && field === "notifySms") next.notifyPhoneNumber = "";
      if (checked && field === "notifyEmail" && selectedCustomer?.email) {
        next.notifyEmailAddress = selectedCustomer.email;
      }
      if (checked && field === "notifySms" && selectedCustomer?.phoneNumber) {
        next.notifyPhoneNumber = selectedCustomer.phoneNumber;
      }
      return next;
    });
    if (!checked) {
      if (field === "schedule") setErrors((prev) => ({ ...prev, startDate: false }));
      if (field === "hasExpiry") setErrors((prev) => ({ ...prev, expiryDate: false }));
      if (field === "notifyEmail") setErrors((prev) => ({ ...prev, notifyEmailAddress: false }));
      if (field === "notifySms") setErrors((prev) => ({ ...prev, notifyPhoneNumber: false }));
    }
  };

  const handleSubmitClick = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {
      amount: !form.amount || !(parseFloat(form.amount) > 0),
      currency: !form.currency,
      description: !form.description,
      quantity: !form.quantity,
      startDate:
        form.schedule &&
        (!form.startDate || isPastDateTime(form.startDate, form.startTime)),
      expiryDate:
        form.hasExpiry &&
        (!form.expiryDate || isPastDateTime(form.expiryDate, form.expiryTime)),
      notifyEmailAddress:
        form.notifyEmail &&
        (!form.notifyEmailAddress.trim() || !emailRegex.test(form.notifyEmailAddress.trim())),
      notifyPhoneNumber: form.notifySms && !form.notifyPhoneNumber.trim(),
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;
    setConfirming(true);
  };

  const handleConfirm = () => {
    const body = {
      order: {
        description: form.description,
        quantity: form.quantity,
      },
      validFromDate: form.schedule ? formatPaymentDate(form.startDate, form.startTime) : formatPaymentDate(null),
      validTillDate: form.hasExpiry ? formatPaymentDate(form.expiryDate, form.expiryTime) : "",
      hasQr: form.attachQr,
      notifyEmail: form.notifyEmail ? 1 : 0,
      notifyEmailAddress: form.notifyEmail ? form.notifyEmailAddress : "",
      notifySms: form.notifySms ? 1 : 0,
      notifyPhoneNumber: form.notifySms ? form.notifyPhoneNumber : "",
      customerId: selectedCustomer?.customerId ?? null,
      customerName: selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : null,
      payment: {
        amount: {
          amountInCents: Math.round(parseFloat(form.amount) * 100),
          currency: form.currency,
        },
        merchant: {
          name: merchant?.companyName || merchant?.tradeName || "",
        },
        paymentType: "PURCHASE",
        merchantTransactionId: generateTransactionId(),
        description: form.description,
        customerId: selectedCustomer?.customerId ?? null,
        customerName: selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : null,
      },
    };

    setCreating(true);
    api.post("payments-link", body)
      .then((res) => {
        toast.success("Payment link created successfully.");
        const data = res.data?.data?.data ?? res.data?.data ?? res.data;
        const pgBase = (process.env.REACT_APP_PAYMENT_GATEWAY_URL || "").replace(/\/$/, "");
        const link = data?.id ? `${pgBase}/api/v1/payments-link/${data.id}` : data?.paymentLink;
        setConfirming(false);
        if (link) {
          setCreatedLink(link);
        } else {
          navigate("/payment-link");
        }
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to create payment link.");
        }
      })
      .finally(() => {
        setCreating(false);
      });
  };

  const inputSx = { mb: 2 };
  const labelSx = { "& .MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: "normal" } };
  const { date: todayStr, time: nowTimeStr } = getNowDateTimeParts();
  const startTimeMin = form.startDate === todayStr ? nowTimeStr : undefined;
  const expiryTimeMins = [
    form.expiryDate === todayStr ? nowTimeStr : null,
    form.schedule && form.expiryDate && form.expiryDate === form.startDate
      ? form.startTime || "00:00"
      : null,
  ].filter(Boolean);
  const expiryTimeMin = expiryTimeMins.length
    ? expiryTimeMins.reduce((a, b) => (a > b ? a : b))
    : undefined;

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle="New Payment Link" />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="flex-start">
          <Grid item xs={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  New Payment Link
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  Create a new payment link
                </MDTypography>
              </MDBox>
              <MDButton variant="gradient" color={sidenavColor} size="small" onClick={handleSubmitClick}>
                <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                Create Payment Link
              </MDButton>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount"
                    type="text"
                    inputMode="decimal"
                    fullWidth
                    size="small"
                    required
                    value={form.amount}
                    onChange={handleAmountChange}
                    onKeyDown={allowDecimalKeys}
                    error={!!errors.amount}
                    helperText={errors.amount ? "Amount must be greater than 0" : ""}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Currency"
                    select
                    fullWidth
                    size="small"
                    required
                    value={form.currency}
                    onChange={set("currency")}
                    error={!!errors.currency}
                    helperText={errors.currency ? "Currency is required" : ""}
                    sx={inputSx}
                    SelectProps={{ sx: { height: "40px" } }}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Description"
                    fullWidth
                    size="small"
                    required
                    value={form.description}
                    onChange={set("description")}
                    error={!!errors.description}
                    helperText={errors.description ? "Description is required" : ""}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Order Quantity"
                    type="number"
                    fullWidth
                    size="small"
                    required
                    value={form.quantity}
                    onChange={handleQuantityChange}
                    onKeyDown={allowIntegerKeys}
                    inputProps={{ min: 1 }}
                    error={!!errors.quantity}
                    helperText={errors.quantity ? "Quantity is required" : ""}
                    disabled={!!selectedCustomer}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              <Autocomplete
                size="small"
                options={Array.isArray(customers) ? customers : []}
                getOptionLabel={(c) => `${c.firstName} ${c.lastName}`}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                renderInput={(params) => (
                  <TextField {...params} label="Select Existing Customer (optional)" sx={{ mb: 1.5 }} />
                )}
              />

              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ mb: 0.5, display: "block" }}>
                Notifications
              </MDTypography>

              <MDBox mb={1}>
                <MDBox display="flex" gap={2} alignItems="flex-start">
                  <MDBox flex={1}>
                    <FormControlLabel
                      sx={labelSx}
                      control={<Checkbox size="small" checked={form.notifyEmail} onChange={toggle("notifyEmail")} />}
                      label="Notify via Email"
                    />
                    <Collapse in={form.notifyEmail} timeout={300} unmountOnExit>
                      <TextField
                        label="Email Address"
                        fullWidth
                        size="small"
                        value={form.notifyEmailAddress}
                        onChange={set("notifyEmailAddress")}
                        error={!!errors.notifyEmailAddress}
                        helperText={errors.notifyEmailAddress ? "Valid email address is required" : ""}
                        sx={{ mt: 0.5 }}
                      />
                    </Collapse>
                  </MDBox>
                </MDBox>
              </MDBox>

              <Divider sx={{ my: 1 }} />

              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ mb: 0.5, display: "block" }}>
                Options
              </MDTypography>

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.schedule} onChange={toggle("schedule")} />}
                    label="Schedule Link"
                  />
                  <Collapse in={form.schedule} timeout={300} unmountOnExit>
                    <MDBox sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        required
                        value={form.startDate}
                        onChange={handleStartDateChange}
                        onClick={() => startDateRef.current?.showPicker?.()}
                        inputRef={startDateRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: todayStr,
                          max: form.expiryDate || undefined,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                          style: { cursor: "pointer", caretColor: "transparent" },
                        }}
                        error={!!errors.startDate}
                        helperText={
                          errors.startDate
                            ? form.startDate
                              ? "Start date/time cannot be in the past"
                              : "Start date is required"
                            : ""
                        }
                        sx={{ flex: 1, cursor: "pointer" }}
                      />
                      <TextField
                        label="Start Time"
                        type="time"
                        size="small"
                        value={form.startTime}
                        onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                        inputRef={startTimeRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: startTimeMin }}
                        error={!!errors.startDate}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>

                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.hasExpiry} onChange={toggle("hasExpiry")} />}
                    label="Expiry"
                  />
                  <Collapse in={form.hasExpiry} timeout={300} unmountOnExit>
                    <MDBox sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <TextField
                        label="Expiry Date"
                        type="date"
                        size="small"
                        required
                        value={form.expiryDate}
                        onChange={handleExpiryDateChange}
                        onClick={() => expiryDateRef.current?.showPicker?.()}
                        inputRef={expiryDateRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: form.startDate || todayStr,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                          style: { cursor: "pointer", caretColor: "transparent" },
                        }}
                        error={!!errors.expiryDate}
                        helperText={
                          errors.expiryDate
                            ? form.expiryDate
                              ? "Expiry date/time cannot be in the past"
                              : "Expiry date is required"
                            : ""
                        }
                        sx={{ flex: 1, cursor: "pointer" }}
                      />
                      <TextField
                        label="Expiry Time"
                        type="time"
                        size="small"
                        value={form.expiryTime}
                        onChange={(e) => setForm((prev) => ({ ...prev, expiryTime: e.target.value }))}
                        inputRef={expiryTimeRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: expiryTimeMin }}
                        error={!!errors.expiryDate}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>

                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.attachQr} onChange={toggle("attachQr")} />}
                    label="Generate QR"
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Confirmation dialog */}
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="medium">Confirm Payment Link</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to create a payment link for{" "}
            <strong>{form.amount} {form.currency}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setConfirming(false)} disabled={creating}>
            Back
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleConfirm} disabled={creating}>
            {creating ? "Creating…" : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <CopyLinkDialog
        open={!!createdLink}
        title="Payment Link Created"
        message="Your payment link is ready. Copy it to share with your customer."
        link={createdLink}
        copyLabel="Copy Payment Link"
        successMessage="Payment link copied!"
        onClose={() => navigate("/payment-link")}
      />
    </DashboardLayout>
  );
}

export default NewPaymentLink;
